import { ReviewProps } from '../types/review'
import { pageCache } from './pageCache'
/**
 * 홈 피드가 저장해 두는 목록 스냅샷.
 *
 * 홈은 목록·페이지·스크롤 위치를 sessionStorage에 담아두고, 다른 화면에
 * 다녀오면 그걸로 되살린다. 리뷰를 보고 돌아온 사용자에게 "방금 보던 화면"을
 * 그대로 돌려주기 위한 것이다.
 *
 * 문제는 내가 뭔가를 바꿨을 때다. 기록을 새로 쓰고 홈으로 가면 그 기록이
 * 피드 맨 위에 있어야 하는데, 스냅샷은 쓰기 전에 찍힌 것이라 없다.
 * 지우는 코드가 어디에도 없어서 계속 옛 목록이 되살아났다.
 *
 * 그래서 목록이 달라졌을 만한 일을 한 뒤에는 이걸 불러 버린다.
 * 스냅샷이 없으면 홈은 평소대로 서버에서 새로 받아온다(기본 탭은 팔로잉).
 */

/** MainHome이 스냅샷을 담는 키. 양쪽이 같아야 하므로 여기 한 곳에만 둔다. */
export const HOME_SNAPSHOT_KEY = 'home_snapshot'

/** MainRanking이 목록을 담는 키의 앞부분 (`ranking_data:<사용자>`) */
const RANKING_CACHE_PREFIX = 'ranking_data'

/** 홈 피드 스냅샷을 버린다. 다음에 홈에 가면 새로 받아온다. */
export function invalidateHomeFeedSnapshot(): void {
  try {
    sessionStorage.removeItem(HOME_SNAPSHOT_KEY)
  } catch {
    // 저장소를 못 쓰는 환경이어도 화면이 멈추면 안 된다
  }
}

/**
 * 자세히보기로 넘어가기 직전에, 지금 화면이 들고 있는 기록을 미리 넘겨준다.
 *
 * 피드는 이미 그 기록을 다 그려 놓고 있다. 그런데 자세히보기는 아무것도 없는
 * 상태로 시작해 처음부터 다시 받아온다. 그래서 넘어가는 순간 화면이 한 번
 * 비었다가 채워진다 — 뒤로가기 때와 똑같은 깜빡임이 앞으로 갈 때도 생긴다.
 *
 * 미리 넣어두면 자세히보기가 첫 프레임부터 내용을 그린다. 조회는 그대로
 * 뒤에서 돌아 최신 값으로 덮으므로, 오래된 내용이 남을 걱정은 없다.
 */
export function seedReviewDetailCache(review: ReviewProps): void {
  pageCache.set(`review-detail:${review.id}`, review)
}

/**
 * 기록이 생기거나 사라지면 화면이 들고 있던 캐시를 전부 버린다.
 *
 * 홈 피드뿐 아니라 랭킹도 목록을 캐시한다(ranking_data:*). 기록을 지웠는데
 * 그 캐시가 남아 있으면 이미 없는 가게가 랭킹에 계속 보인다.
 * 어느 화면으로 가든 새로 받아오게 한 번에 비운다.
 */
export function invalidateReviewCaches(): void {
  invalidateHomeFeedSnapshot()
  // 기록 화면들이 들고 있는 캐시도 같이 버린다.
  // 안 버리면 지운 기록이 자세히보기·사진 화면에 그대로 남는다.
  pageCache.clearPrefix('review-detail:')
  pageCache.clearPrefix('review-comments:')
  pageCache.clearPrefix('review-photo:')
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(RANKING_CACHE_PREFIX)) {
        sessionStorage.removeItem(key)
      }
    }
  } catch {
    // 위와 같은 이유
  }
}
